// ============================================================================
// Componente: FinanceiroFitaImportacao.js
// Objetivo: Importar o Relatório Analítico de Conciliação (Resumo de Vendas)
//           da Atos para o banco, alimentando a Conciliação da Fita.
// Padrão visual: Financeiro / Verde-azulado (estilo apps de automação)
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import {
    Database,
    AlertCircle,
    CheckCircle2,
    Loader2,
    FileSpreadsheet,
    Eraser,
    UploadCloud,
    Upload
} from 'lucide-react';
import FinanceiroFitaCobertura from './FinanceiroFitaCobertura';
import FinanceiroFitaHistorico from './FinanceiroFitaHistorico';

const FinanceiroFitaImportacao = () => {
    const corModulo = '#16a39c';
    const corModuloEscura = '#0f766e';
    const corFundoClaro = '#f0fdfa';
    const corBorda = '#99f6e4';

    const inputCsvRef = useRef(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [arquivoCsv, setArquivoCsv] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [mensagem, setMensagem] = useState(null);

    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando arquivo...');
    const [isPolling, setIsPolling] = useState(false);
    const [executionId, setExecutionId] = useState(null);
    const [resumo, setResumo] = useState([]);
    const [refreshCobertura, setRefreshCobertura] = useState(0);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Limpa um nome de loja/prefixo de log: remove timestamp e nível do logging do Python
    const limparLinha = (linha) =>
        String(linha)
            .replace(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3}\s*-\s*\w+\s*-\s*/, '')
            .replace(/^\[.*?\]\s*/, '')
            .trim();

    useEffect(() => {
        let interval;

        if (isPolling && executionId) {
            interval = setInterval(async () => {
                try {
                    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
                    const res = await fetch(`/api/fita/importacao/logs/${executionId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (!res.ok) return;

                    const data = await res.json();
                    if (data.logs && data.logs.length > 0) {
                        const linhasLimpas = data.logs.map(limparLinha).filter(Boolean);
                        setResumo(linhasLimpas);

                        const ultima = linhasLimpas[linhasLimpas.length - 1] || '';
                        setStatusText(ultima);

                        // Mapeia o andamento a partir das mensagens do script
                        const texto = linhasLimpas.join('\n');
                        if (/conclu[ií]da com sucesso|inseridos com sucesso/i.test(texto)) setProgress(100);
                        else if (/Deduplica/i.test(texto)) setProgress(65);
                        else if (/Leitura conclu/i.test(texto)) setProgress(35);
                        else if (/Importando arquivo|Iniciando/i.test(texto)) setProgress(15);
                    }
                } catch (_) {}
            }, 1000);
        }

        return () => clearInterval(interval);
    }, [isPolling, executionId]);

    const handleLimpar = () => {
        setArquivoCsv(null);
        setMensagem(null);
        setProgress(0);
        setStatusText('Aguardando arquivo...');
        setExecutionId(null);
        setIsPolling(false);
        setResumo([]);

        if (inputCsvRef.current) inputCsvRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!arquivoCsv) {
            setMensagem({ type: 'error', text: 'Selecione o arquivo CSV da Atos antes de importar.' });
            return;
        }

        setCarregando(true);
        setMensagem(null);
        setProgress(5);
        setStatusText('Enviando arquivo CSV...');
        setResumo([]);

        const newExecutionId = `fita_import_${Date.now()}`;
        setExecutionId(newExecutionId);
        setIsPolling(true);

        const formData = new FormData();
        formData.append('executionId', newExecutionId);
        formData.append('arquivo_csv', arquivoCsv);

        try {
            const token = localStorage.getItem('token') || localStorage.getItem('authToken');
            const response = await fetch('/api/fita/importar-resumo-vendas', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.error || 'Erro ao importar o arquivo.');
            }

            setProgress(100);
            setStatusText('Importação concluída!');
            setMensagem({ type: 'success', text: 'Arquivo importado com sucesso! Os dados já estão disponíveis na Conciliação.' });
            setArquivoCsv(null);
            if (inputCsvRef.current) inputCsvRef.current.value = '';
            setRefreshCobertura(c => c + 1); // atualiza o painel de cobertura
        } catch (error) {
            setMensagem({ type: 'error', text: error.message || 'Erro de conexão com o servidor.' });
            setStatusText('Falha na importação.');
        } finally {
            setCarregando(false);
            setTimeout(() => setIsPolling(false), 3000);
        }
    };

    const getProgressBarColor = () => {
        if (mensagem?.type === 'error') return '#ef4444';
        if (mensagem?.type === 'success') return '#10b981';
        return corModulo;
    };

    const cardStyle = {
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: isMobile ? '20px' : '28px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04)'
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={cardStyle}>
                <div style={{ backgroundColor: corFundoClaro, borderLeft: `4px solid ${corModulo}`, padding: '16px 20px', borderRadius: '10px', marginBottom: '24px' }}>
                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: corModuloEscura, fontSize: '15px', fontWeight: '800', margin: 0 }}>
                        <Database size={18} /> Importação do Resumo de Vendas (Atos)
                    </h4>
                    <p style={{ color: '#115e59', fontSize: '14px', lineHeight: '1.6', margin: '6px 0 0 0' }}>
                        Faça o upload do CSV da Atos — o sistema identifica automaticamente se é o <strong>Resumo de Vendas</strong> (cartões/PIX) ou o de <strong>Recebimentos</strong> (dinheiro) e importa para o banco. Os dados ficam disponíveis na aba de Conciliação. Reimportar o mesmo período substitui os registros existentes (sem duplicar).
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{
                            border: `2px dashed ${corBorda}`,
                            borderRadius: '14px',
                            padding: '40px 20px',
                            textAlign: 'center',
                            backgroundColor: '#f8fafc',
                            cursor: carregando ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            position: 'relative'
                        }}>
                            <input
                                ref={inputCsvRef}
                                type="file"
                                accept=".csv"
                                onChange={(e) => {
                                    setArquivoCsv(e.target.files?.[0] || null);
                                    setMensagem(null);
                                    setProgress(0);
                                    setStatusText('Aguardando arquivo...');
                                    setResumo([]);
                                }}
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: carregando ? 'not-allowed' : 'pointer' }}
                                required
                                disabled={carregando}
                            />

                            {!arquivoCsv ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ backgroundColor: '#f1f5f9', padding: '16px', borderRadius: '50%', color: '#94a3b8' }}>
                                        <UploadCloud size={32} />
                                    </div>
                                    <div>
                                        <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#334155' }}>Arraste o CSV aqui ou clique para selecionar</p>
                                        <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>Formato suportado: CSV (Atos)</p>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ backgroundColor: corFundoClaro, padding: '16px', borderRadius: '50%', color: corModulo }}>
                                        <FileSpreadsheet size={32} />
                                    </div>
                                    <div>
                                        <p style={{ margin: '0 0 4px 0', fontWeight: '800', color: '#0f172a' }}>{arquivoCsv.name}</p>
                                        <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{(arquivoCsv.size / 1024 / 1024).toFixed(2)} MB</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            onClick={handleLimpar}
                            disabled={carregando}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 18px', borderRadius: '8px', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '800', border: '1px solid #cbd5e1', cursor: carregando ? 'not-allowed' : 'pointer', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}
                        >
                            <Eraser size={18} /> Limpar
                        </button>

                        <button
                            type="submit"
                            disabled={carregando || !arquivoCsv}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', backgroundColor: carregando || !arquivoCsv ? '#cbd5e1' : corModulo, color: '#fff', fontWeight: '800', border: 'none', cursor: carregando || !arquivoCsv ? 'not-allowed' : 'pointer', boxShadow: '0 4px 8px -3px rgba(22, 163, 156, 0.45)', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}
                        >
                            {carregando ? <><Loader2 size={18} className="animate-spin" /> Importando...</> : <><Upload size={18} /> Importar para o Banco</>}
                        </button>
                    </div>
                </form>

                {mensagem && !carregando && (
                    <div style={{
                        backgroundColor: mensagem.type === 'success' ? '#ecfdf5' : '#fef2f2',
                        border: `1px solid ${mensagem.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
                        color: mensagem.type === 'success' ? '#047857' : '#b91c1c',
                        padding: '16px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginTop: '24px',
                        fontSize: '14px',
                        fontWeight: '700'
                    }}>
                        {mensagem.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                        {mensagem.text}
                    </div>
                )}
            </div>

            {(carregando || progress > 0) && (
                <div style={cardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {carregando && <Loader2 size={24} color={corModulo} className="animate-spin" />}
                            {!carregando && mensagem?.type === 'success' && <CheckCircle2 size={24} color="#10b981" />}
                            {!carregando && mensagem?.type === 'error' && <AlertCircle size={24} color="#ef4444" />}
                            <div>
                                <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#1e293b' }}>Status da Importação</h4>
                                <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>{statusText}</p>
                            </div>
                        </div>
                        <span style={{ fontSize: '18px', fontWeight: '900', color: getProgressBarColor() }}>{Math.round(progress)}%</span>
                    </div>

                    <div style={{ width: '100%', height: '10px', backgroundColor: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(progress, 100)}%`, height: '100%', backgroundColor: getProgressBarColor(), transition: 'width 0.4s ease' }} />
                    </div>

                    {resumo.length > 0 && (
                        <div style={{
                            marginTop: '20px',
                            backgroundColor: '#0f172a',
                            borderRadius: '10px',
                            padding: '14px 16px',
                            maxHeight: '180px',
                            overflowY: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '12.5px',
                            lineHeight: '1.7',
                            color: '#cbd5e1'
                        }}>
                            {resumo.map((linha, i) => (
                                <div key={i} style={{ whiteSpace: 'pre-wrap' }}>
                                    <span style={{ color: '#475569' }}>›</span> {linha}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Painel de cobertura: o que já foi importado e o que falta */}
            <FinanceiroFitaCobertura refreshKey={refreshCobertura} />

            {/* Histórico de importações: quem importou e quando */}
            <FinanceiroFitaHistorico refreshKey={refreshCobertura} />
        </div>
    );
};

export default FinanceiroFitaImportacao;
